/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faEarthAmericas,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { Menu, MenuItem } from '@mui/material';
import type { CSSObject } from '@emotion/serialize';
import Typography, {
  TypographyVariant,
} from '../../../component_library/components/Typography';
import { useLanguagePicker } from './hooks';
import {
  DataElementId,
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import {
  Button,
  ButtonVariant,
} from '../../../component_library/components/Button';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

const LANGUAGE_DROPDOWN_HTML_ID = 'language-dropdown';

const MENU_OPEN_DELAY_MILLISECONDS = 350;

interface ILanguagePicker {
  hideTitle?: boolean;
  useDarkFontColor?: boolean;
}

const LanguagePicker: React.FC<ILanguagePicker> = ({
  hideTitle = false,
  useDarkFontColor = false,
}) => {
  const {
    language, setLanguage, availableLanguages, theme,
  } =
    useLanguagePicker();
  const themeToken = useThemeToken();
  const menuLastOpenedTimeMillis = useRef<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const isOpen = !!anchorEl;

  const currentLanguageName =
    availableLanguages.find((item) => item.languageCode === language)
      ?.languageName ?? language;

  const onLanguageChange = useCallback(
    (newLanguageCode: string) => {
      setAnchorEl(null);
      setLanguage(newLanguageCode);
    },
    [setLanguage],
  );

  const handleCloseTooltip = useCallback(() => {
    const nowMillis = Date.now();
    if (
      nowMillis - menuLastOpenedTimeMillis.current <
      MENU_OPEN_DELAY_MILLISECONDS
    ) {
      // Disallow immediately closing the menu.
      // This fixes a bug where pressing the button to open the menu on Android
      // instantly triggers the onClose function with reason `backdropClick`,
      // which prevents the menu from appearing.
      return;
    }
    setAnchorEl(null);
  }, []);

  const handleOpenTooltip: React.MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget);
      menuLastOpenedTimeMillis.current = Date.now();
    },
    [],
  );

  const fontColor = useDarkFontColor
    ? theme.colors.secondary
    : theme.colors.grayscale.white;

  const iconStyles: CSSObject = {
    transition: `all ${MENU_OPEN_DELAY_MILLISECONDS / 1000}s ease-in-out`,
    fontSize: '10px',
  };

  const transformIconStyles: CSSObject = {
    transform: 'rotate(180deg)',
  };

  if (availableLanguages.length < 2) {
    return null;
  }

  return (
    <div>
      <Button
        id={LANGUAGE_DROPDOWN_HTML_ID}
        onClick={handleOpenTooltip}
        dataElementId={DataElementId.LANGUAGE_PICKER_BUTTON_DROPDOWN}
        type="button"
        themeToken={themeToken}
        variant={ButtonVariant.FLAT}
        hideDecorations
        inlineButton
        icon={<FontAwesomeIcon icon={faEarthAmericas} />}
        color={fontColor}
        additionalStyles={{
          display: 'flex',
          alignItems: 'center',
          columnGap: `${themeToken.marginXS}px`,
        }}
      >
        {!hideTitle && (
          <Typography
            variant={TypographyVariant.SMALL_BODY_BOLD}
            color="inherit"
            theme={theme}
          >
            {currentLanguageName}
          </Typography>
        )}
        <FontAwesomeIcon
          icon={faChevronDown}
          css={[iconStyles, isOpen ? transformIconStyles : '']}
        />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseTooltip}
        transitionDuration={{
          appear: MENU_OPEN_DELAY_MILLISECONDS,
          enter: MENU_OPEN_DELAY_MILLISECONDS,
          exit: 0,
        }}
        MenuListProps={{
          'aria-labelledby': LANGUAGE_DROPDOWN_HTML_ID,
        }}
      >
        {availableLanguages.map(({ languageCode, languageName }) => (
          <MenuItem
            key={languageCode}
            onClick={() => onLanguageChange(languageCode)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
            {...{
              [ELEMENT_ID_DATA_LOG_ATTRIBUTE]:
                DataElementId.HEADER_LANGUAGE_PICKER_LANGUAGE_RADIO_OPTION,
              [ELEMENT_ID_DATA_TEST_ATTRIBUTE]:
                DataElementId.HEADER_LANGUAGE_PICKER_LANGUAGE_RADIO_OPTION,
            }}
          >
            <Typography variant={TypographyVariant.SMALL_BODY_BOLD}>
              {languageName}
            </Typography>
            {languageName === currentLanguageName ? (
              <FontAwesomeIcon
                icon={faCheck}
                color={theme.colors.secondary}
                css={{ marginLeft: '8px' }}
              />
            ) : (
              <div />
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default LanguagePicker;
